import type { DetrazioneCalcolata } from '../../services/detrazioniCalculations';
import { formatCurrency } from '../../services/detrazioniCalculations';

interface DetrazioniCardProps {
  detrazione: DetrazioneCalcolata;
}

export function DetrazioniCard({ detrazione }: DetrazioniCardProps) {
  const { config, speseAmmesse, detrazioneTotale, detrazioneAnnua, preventivi } = detrazione;

  // Colori per tipo di bonus
  const colorMap: Record<string, { bg: string; text: string; border: string }> = {
    ristrutturazione: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
    mobili: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
    ecobonus: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
    verde: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
    sismabonus: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' }
  };

  const colors = colorMap[config.id] || { bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-200' };

  return (
    <div className={`rounded-lg border ${colors.border} ${colors.bg} p-4`}>
      {/* Header */}
      <div className="flex justify-between items-start mb-3">
        <div>
          <h4 className={`font-semibold ${colors.text}`}>
            {config.nome}
          </h4>
          <p className="text-sm text-gray-600">{config.descrizione}</p>
        </div>
        <span className={`px-2 py-1 rounded text-sm font-bold ${colors.text} ${colors.bg}`}>
          {config.percentuale}%
        </span>
      </div>

      {/* Amounts */}
      <div className="grid grid-cols-3 gap-4 mb-3">
        <div>
          <p className="text-xs text-gray-500 uppercase">Spese ammesse</p>
          <p className="font-semibold text-gray-900">{formatCurrency(speseAmmesse)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500 uppercase">Detrazione totale</p>
          <p className={`font-semibold ${colors.text}`}>{formatCurrency(detrazioneTotale)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500 uppercase">Per {config.anni} anni</p>
          <p className="font-semibold text-gray-900">{formatCurrency(detrazioneAnnua)}/anno</p>
        </div>
      </div>

      {/* Massimale info */}
      {speseAmmesse >= config.massimoDetraibile && (
        <div className="mb-3 text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded">
          Raggiunto il massimale di {formatCurrency(config.massimoDetraibile)}
        </div>
      )}

      {/* Linked items */}
      {preventivi.length > 0 && (
        <div>
          <p className="text-xs text-gray-500 mb-1">Voci preventivo incluse:</p>
          <div className="flex flex-wrap gap-1">
            {preventivi.slice(0, 5).map(p => (
              <span
                key={p.id}
                className="text-xs bg-white px-2 py-0.5 rounded border"
              >
                {p.voce}
              </span>
            ))}
            {preventivi.length > 5 && (
              <span className="text-xs text-gray-500">
                +{preventivi.length - 5} altre
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
